import type { RunUsage, ThreadMessage } from "@rakazo/contracts";
import { canReactToThreadMessage } from "@rakazo/contracts";
import { formatCompactDuration } from "./routine-runs.js";

type ReplyMessage = Pick<ThreadMessage, "id" | "role" | "runId" | "blocks">;

/** Formats a USD amount for `locale`; spend below a cent reads "<$0.01" and unknown spend "—". */
export function usdFormatter(locale: string): (value: number | null | undefined) => string {
  const usd = new Intl.NumberFormat(locale, { style: "currency", currency: "USD" });
  return (value) =>
    value == null ? "—" : value > 0 && value < 0.01 ? `<${usd.format(0.01)}` : usd.format(value);
}

function isReply(message: ReplyMessage): boolean {
  return (
    message.role === "bot" &&
    !message.id.includes(":") &&
    canReactToThreadMessage(message) &&
    message.blocks.some((block) => block.kind === "text")
  );
}

/**
 * The replies that carry ratings and usage: a bot's finished text messages, once per run on its
 * last such message, so a run that narrated along the way is rated as one reply.
 */
export function replyFooterIds(messages: readonly ReplyMessage[]): Set<string> {
  const ids = new Set<string>();
  const lastOfRun = new Map<string, string>();
  for (const message of messages) {
    if (!isReply(message)) continue;
    if (message.runId) lastOfRun.set(message.runId, message.id);
    else ids.add(message.id);
  }
  for (const id of lastOfRun.values()) ids.add(id);
  return ids;
}

/** "1,240 → 380 · $0.02 · model · 4.2s" from what a reply's run spent. */
export function formatRunUsage(usage: RunUsage, locale: string): string {
  const count = new Intl.NumberFormat(locale);
  const parts = [`${count.format(usage.inputTokens)} → ${count.format(usage.outputTokens)}`];
  if (usage.costUsd != null) parts.push(usdFormatter(locale)(usage.costUsd));
  if (usage.model) parts.push(usage.model);
  const duration = formatCompactDuration(usage.durationMs);
  if (duration) parts.push(duration);
  return parts.join(" · ");
}
